// Filename resolution for CSV downloads (Stripe payouts, flag
// REACT_APP_STRIPE_PAYOUTS_ENABLED). The API sets Content-Disposition with a
// FileDownloadName, but the browser only exposes the header to fetch() when
// the API lists it in Access-Control-Expose-Headers - so a caller must always
// have a fallback that reproduces the server's naming.

// Parses `attachment; filename="x.csv"` and the RFC 5987 form
// `filename*=UTF-8''x%20y.csv` (the latter wins when both are present).
// Returns undefined for a missing/unknown header or an empty name.
export function filenameFromContentDisposition(
  header: string | null | undefined,
): string | undefined {
  if (!header) return undefined;
  const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (extended && extended[1]) {
    try {
      const decoded = decodeURIComponent(extended[1].trim());
      if (decoded !== "") return stripPath(decoded);
    } catch {
      // Malformed percent-encoding - fall through to the plain form.
    }
  }
  const plain = /filename\s*=\s*"([^"]*)"|filename\s*=\s*([^;]+)/.exec(header);
  const name = (plain?.[1] ?? plain?.[2] ?? "").trim();
  return name === "" ? undefined : stripPath(name);
}

function stripPath(name: string): string {
  const cut = Math.max(name.lastIndexOf("/"), name.lastIndexOf("\\"));
  return cut >= 0 ? name.substring(cut + 1) : name;
}

// The server's own naming (StripePayoutAdminFunctions):
// stripe-payout-{payoutId}-{yyyyMMdd}.csv, date = the download day.
export function stripePayoutCsvFallbackName(
  payoutId: string,
  now: Date = new Date(),
): string {
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const safeId = payoutId.replace(/[^A-Za-z0-9_-]/g, "_");
  return `stripe-payout-${safeId}-${yyyy}${mm}${dd}.csv`;
}

export function resolveCsvFilename(
  contentDisposition: string | null | undefined,
  fallback: string,
): string {
  return filenameFromContentDisposition(contentDisposition) ?? fallback;
}
